import { createFileRoute } from "@tanstack/react-router";
import { Layout } from "@/components/site/Layout";
import { Reveal } from "@/components/site/Reveal";
import { Eyebrow } from "@/components/site/Eyebrow";
import { breadcrumbScript } from "@/lib/schema";

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { title: "Privacy Policy | Avolus Cars Vancouver" },
      {
        name: "description",
        content:
          "How Avolus Cars collects, uses and protects personal information from booking and contact forms, cookies and analytics, and how to request deletion.",
      },
      { property: "og:title", content: "Privacy Policy | Avolus Cars" },
      {
        property: "og:description",
        content:
          "What data Avolus Cars collects, why, how long it is kept, and how to request access or deletion.",
      },
      { property: "og:url", content: "https://avoluscars.com/privacy" },
    ],
    links: [{ rel: "canonical", href: "https://avoluscars.com/privacy" }],
    scripts: [
      breadcrumbScript([
        { name: "Home", url: "https://avoluscars.com/" },
        { name: "Privacy Policy", url: "https://avoluscars.com/privacy" },
      ]),
    ],
  }),
  component: Privacy,
});

const EFFECTIVE = "September 14, 2026";

function Privacy() {
  return (
    <Layout>
      <section className="pt-40 pb-12">
        <div className="mx-auto max-w-3xl px-6 lg:px-10">
          <Reveal>
            <Eyebrow>Legal</Eyebrow>
            <h1 className="font-display text-5xl md:text-6xl text-foreground">Privacy Policy</h1>
            <p className="mt-6 text-foreground/70">
              Avolus Cars is a delivery-first luxury car rental in Vancouver, BC. This policy
              explains what personal information we collect through avoluscars.com, why we
              collect it, who it is shared with, and how you can access or delete it.
            </p>
            <p className="mt-3 text-xs uppercase tracking-[0.25em] text-muted-foreground">
              Effective {EFFECTIVE}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-24">
        <div className="mx-auto max-w-3xl px-6 lg:px-10 space-y-10 text-sm leading-relaxed text-foreground/80">
          <Reveal>
            <h2 className="font-display text-2xl text-foreground mb-3">Who we are</h2>
            <p>
              Avolus Cars, 250 - 997 Seymour St #1711, Vancouver, BC V6B 3M1, Canada. Mailing
              address only. We are a delivery service with no physical pickup counter. Phone{" "}
              <a href="tel:+17783798881" className="text-foreground underline underline-offset-4">
                +1-778-379-8881
              </a>
              , email{" "}
              <a href="mailto:info@avoluscars.com" className="text-foreground underline underline-offset-4">
                info@avoluscars.com
              </a>
              . We are the organisation responsible for the personal information described here.
            </p>
          </Reveal>

          <Reveal>
            <h2 className="font-display text-2xl text-foreground mb-3">Information we collect</h2>
            <ul className="list-disc pl-5 space-y-2">
              <li>
                <strong className="text-foreground">Contact enquiries.</strong> Name, email
                address, phone number and the content of your message when you submit our
                contact form.
              </li>
              <li>
                <strong className="text-foreground">Reservations.</strong> Booking details such
                as vehicle, dates, delivery location, driver details and payment authorisation,
                collected through our reservation system provider.
              </li>
              <li>
                <strong className="text-foreground">Rental verification.</strong> Where a rental
                proceeds, a valid driver's licence, proof of insurance and a credit card in the
                renter's name, as required to release a vehicle.
              </li>
              <li>
                <strong className="text-foreground">Site usage.</strong> If you accept analytics
                cookies, aggregated data such as pages viewed, approximate region, device type
                and referring source.
              </li>
            </ul>
          </Reveal>

          <Reveal>
            <h2 className="font-display text-2xl text-foreground mb-3">Why we use it</h2>
            <p>
              To respond to enquiries, prepare and fulfil rental agreements, verify eligibility
              to drive, arrange vehicle delivery and collection, process payments and deposits,
              meet legal and insurance obligations, and improve the website. We do not sell
              personal information, and we do not use it for automated decision-making.
            </p>
          </Reveal>

          <Reveal>
            <h2 className="font-display text-2xl text-foreground mb-3">Who it is shared with</h2>
            <p>
              We share only what is necessary with service providers acting on our behalf: our
              reservation and rental management platform, our website and database hosting
              provider, our email delivery provider, payment processors, and our insurer. We may
              also disclose information where required by law, by a court order, or to
              establish or defend a legal claim. Some of these providers process data outside
              Canada, where local law may permit access by authorities in that jurisdiction.
            </p>
          </Reveal>

          <Reveal>
            <h2 className="font-display text-2xl text-foreground mb-3">Cookies and analytics</h2>
            <p>
              Essential cookies keep the site and booking engine working and are always active.
              Analytics cookies (Google Analytics) are set only after you select "Accept" in our
              cookie banner; choosing "Decline" leaves analytics storage disabled through Google
              Consent Mode. You can change your mind at any time by clearing this site's data in
              your browser, which makes the banner appear again. Our booking engine is embedded
              from a third-party provider and sets its own cookies when you interact with it.
            </p>
          </Reveal>

          <Reveal>
            <h2 className="font-display text-2xl text-foreground mb-3">How long we keep it</h2>
            <p>
              Contact enquiries are kept for up to 24 months. Rental and payment records are kept
              for seven years to meet tax, insurance and liability requirements. Analytics data is
              retained in aggregate according to our Google Analytics retention setting. When a
              retention period ends, records are deleted or anonymised.
            </p>
          </Reveal>

          <Reveal>
            <h2 className="font-display text-2xl text-foreground mb-3">Security</h2>
            <p>
              Data is transmitted over HTTPS and stored with access-controlled hosting providers.
              Access is limited to staff who need it to operate the rental business. No system is
              perfectly secure, but we notify affected individuals where a breach creates a real
              risk of significant harm, as required under Canadian privacy law.
            </p>
          </Reveal>

          <Reveal>
            <h2 className="font-display text-2xl text-foreground mb-3">Your rights</h2>
            <p>
              You may request a copy of the personal information we hold about you, ask us to
              correct it, or ask us to delete it where we are not required to keep it. Email{" "}
              <a href="mailto:info@avoluscars.com" className="text-foreground underline underline-offset-4">
                info@avoluscars.com
              </a>{" "}
              and we will respond within 30 days. If you are not satisfied with our response, you
              may contact the Office of the Privacy Commissioner of Canada or the Office of the
              Information and Privacy Commissioner for British Columbia.
            </p>
          </Reveal>

          <Reveal>
            <h2 className="font-display text-2xl text-foreground mb-3">Children</h2>
            <p>
              Our services are intended for licensed drivers aged 25 and over. We do not
              knowingly collect personal information from children.
            </p>
          </Reveal>

          <Reveal>
            <h2 className="font-display text-2xl text-foreground mb-3">Changes to this policy</h2>
            <p>
              We may update this policy as our services change. The effective date above reflects
              the current version. Material changes will be posted on this page.
            </p>
          </Reveal>

          <Reveal>
            <p className="border-t border-border pt-8">
              See also our{" "}
              <a href="/terms" className="text-foreground underline underline-offset-4">
                Terms and Conditions
              </a>
              .
            </p>
          </Reveal>
        </div>
      </section>
    </Layout>
  );
}
